import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { authClient, oauthCallbackURL } from '../lib/auth';
import { selectAuthUser, selectAuthSessionStatus } from '../redux/auth/auth.selectors';

// Auto-triggers the Google One Tap prompt (Better Auth oneTapClient plugin) once
// per mount, only when the visitor is confirmed signed out. On success Better
// Auth redirects to callbackURL and the app re-bootstraps the session, matching
// the existing social sign-in flow. Any prompt failure is swallowed so the page
// never breaks — users fall back to the existing sign-in buttons.
//
// The trigger waits for the session check to settle: `sessionStatus` starts as
// `null` (unknown, checkUserSession still in flight) and resolves to 'authorized'
// or 'not_authorized'. Gating on 'not_authorized' means an already-signed-in user
// (who resolves to 'authorized') never sees the prompt — avoiding the race where
// the still-null `currentUser` on first render would otherwise fire One Tap.
export function useGoogleOneTap({ enabled = true } = {}) {
    const user = useSelector(selectAuthUser);
    const sessionStatus = useSelector(selectAuthSessionStatus);
    const startedRef = useRef(false);

    useEffect(() => {
        if (!enabled || user || sessionStatus !== 'not_authorized' || startedRef.current) return;
        startedRef.current = true;

        authClient.oneTap({ callbackURL: oauthCallbackURL() }).catch(() => {
            // Swallowed by design — a failed prompt must never break the page.
            // Users always have the regular sign-in buttons as a fallback.
        });

        // Dismiss any open One Tap prompt on unmount (e.g. a client-side redirect
        // away from the sign-in page) so it can't linger over the next screen.
        return () => {
            window.google?.accounts?.id?.cancel?.();
        };
    }, [enabled, user, sessionStatus]);
}
